"use client";

import { useCallback, useEffect, useState } from "react";
import type { StreakSummary } from "@/lib/streak/types";

// Shared /api/streak fetch. The nav badge, streak card, and rank card all read
// the same streak — so a module-level cache + in-flight dedup gives them ONE
// network request instead of three, and keeps them in lockstep on refresh.

let cached: StreakSummary | null = null;
let inflight: Promise<StreakSummary | null> | null = null;
let refetchQueued = false;
const listeners = new Set<(s: StreakSummary | null) => void>();

function notify() {
  for (const l of listeners) l(cached);
}

function load(force = false): Promise<StreakSummary | null> {
  if (cached && !force) return Promise.resolve(cached);
  // A forced refresh while a fetch is already running must NOT coalesce into
  // that (possibly pre-mutation) request — queue a fresh read to run once it
  // settles, so a post-build refresh always picks up the new streak.
  if (inflight) {
    if (force) refetchQueued = true;
    return inflight;
  }
  inflight = fetch("/api/streak", { cache: "no-store" })
    .then((r) => (r.ok ? (r.json() as Promise<StreakSummary>) : null))
    .then((data) => {
      cached = data;
      notify();
      return cached;
    })
    .catch(() => cached)
    .finally(() => {
      inflight = null;
      if (refetchQueued) {
        refetchQueued = false;
        void load(true);
      }
    });
  return inflight;
}

/** Force a re-fetch of the streak (e.g. after a build). */
export function refreshStreak(): void {
  void load(true);
}

/** Tell every streak surface a build likely changed the streak. */
export function dispatchStreakUpdate(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("panda:streak-update"));
  }
}

export interface UseStreakResult {
  data: StreakSummary | null;
  loading: boolean;
  refresh: () => void;
}

export function useStreak(): UseStreakResult {
  const [data, setData] = useState<StreakSummary | null>(cached);
  const [loading, setLoading] = useState(cached === null);

  const refresh = useCallback(() => refreshStreak(), []);

  useEffect(() => {
    let alive = true;
    const listener = (s: StreakSummary | null) => {
      if (!alive) return;
      setData(s);
      setLoading(false);
    };
    listeners.add(listener);

    // Resolve the current cache or kick off a fetch — always via the async
    // load() so no setState runs synchronously in the effect body. load()
    // resolves immediately to the cached value when one exists (no flash).
    void load().then((s) => {
      if (!alive) return;
      setData(s);
      setLoading(false);
    });

    // Keep the streak live after a build (a build both charges a credit and
    // extends the streak) and when the user returns to the tab.
    const onUpdate = () => refreshStreak();
    const onFocus = () => refreshStreak();
    window.addEventListener("panda:streak-update", onUpdate);
    window.addEventListener("panda:credit-update", onUpdate);
    window.addEventListener("focus", onFocus);

    return () => {
      alive = false;
      listeners.delete(listener);
      window.removeEventListener("panda:streak-update", onUpdate);
      window.removeEventListener("panda:credit-update", onUpdate);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return { data, loading, refresh };
}
